import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Collapse,
  Container,
  Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

import {
  getCourseByIdForUsers,
  getCoursesState,
  resetUserCourse,
} from '../../../store/V2/CoursesSlice';
import {
  ISectionForUser,
  getSectionsState,
  getUserCourseSectionsByCourseId,
  resetUserCourseSections,
} from '../../../store/V2/SectionSlice';
import { TP_COLORS } from '../../../constants';
import { useAppSelector } from '../../../hooks';
import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../components/BreadCrumb/Breadcrumbs';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import SubscriptionCard from './components/SubscriptionCard';
import ClassDetailViewer from './components/ClassDetailViewer';
import SectionItem from './components/SectionItem';

const ViewCoursePage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const urlParams = useParams();
  const location = useLocation();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));

  // Store
  const coursesState = useAppSelector(getCoursesState);
  const sectionsState = useAppSelector(getSectionsState);

  // State
  const [alertVisibility, setAlertVisibility] = useState(true);
  const [openedSections, setOpenedSections] = useState<Array<string>>([]);
  const [isClassListCollapseOpen, setClassListCollapseOpen] = useState(true);
  const [isClassDetailsCollapseOpen, setClassDetailsCollapseOpen] = useState(
    true
  );

  const courseId = urlParams.id;
  const courseData = coursesState.userCourse;
  const courseSections = sectionsState.userCourseSections || [];
  const questyStringParams = new URLSearchParams(location.search);
  const isSubscriptionVisible =
    courseData &&
    courseData.amount &&
    courseData.currency &&
    !courseData.isSubscribed;

  const isAllSectionsOpened = courseSections.every((item) =>
    openedSections.some((id) => id === item._id)
  );

  let numberOfClassesAndDurationText = '0 Class';

  if (courseData && courseData.noOfClasses) {
    numberOfClassesAndDurationText = `${courseData.noOfClasses} Class`;

    if (courseData.noOfClasses > 1) {
      numberOfClassesAndDurationText += 'es';
    }

    if (courseData.duration) {
      const _totalDuration = courseData.noOfClasses * courseData.duration;
      const _hours = Math.floor(_totalDuration / 60);
      const _mins = _totalDuration % 60;

      let _formatedDuration = '';

      if (_hours) {
        _formatedDuration = `${_hours} hr`;

        if (_mins) {
          _formatedDuration += ` ${_mins} min`;
        }
      } else {
        _formatedDuration += ` ${_mins} min`;
      }

      numberOfClassesAndDurationText += ` | ${_formatedDuration}`;
    }
  }

  useEffect(() => {
    if (courseId) {
      dispatch(getCourseByIdForUsers(courseId));
      dispatch(getUserCourseSectionsByCourseId(courseId));
    }

    return () => {
      dispatch(resetUserCourse());
      dispatch(resetUserCourseSections());
    };
  }, []);

  const toggleClassDetailsCollapse = () => {
    setClassDetailsCollapseOpen(!isClassDetailsCollapseOpen);
  };

  const toggleClassListCollapse = () => {
    setClassListCollapseOpen(!isClassListCollapseOpen);
  };

  const handleSectionCollapse = (section: ISectionForUser) => {
    let newOpenedSections = [...openedSections];

    const match = newOpenedSections.some((item) => item === section._id);

    if (match) {
      newOpenedSections = newOpenedSections.filter(
        (item) => item !== section._id
      );
    } else {
      newOpenedSections.push(section._id);
    }

    setOpenedSections(newOpenedSections);
  };

  const handleCollapseAllSections = () => {
    setOpenedSections((prevState) => {
      if (isAllSectionsOpened) {
        return [];
      }

      const newList = [...prevState];

      courseSections.forEach((item) => {
        if (!newList.some((id) => id === item._id)) {
          newList.push(item._id);
        }
      });

      return newList;
    });
  };

  const constructBreadcrumbOptions = () => {
    const options: Array<BreadcrumbOption> = [
      {
        type: 'link',
        title: 'Home',
        link: `${process.env.PUBLIC_URL}`,
      },
      {
        type: 'link',
        title: 'Courses',
        link: `${process.env.PUBLIC_URL}/portal/courses`,
      },
    ];

    if (courseData) {
      options.push({
        type: 'link',
        title: courseData.title,
        highlight: true,
      });
    }

    return options;
  };

  const renderAlerts = () => {
    if (courseData) {
      if (
        questyStringParams.get('paymentStatus') === 'success' &&
        courseData.isSubscribed
      ) {
        return (
          <Collapse in={alertVisibility}>
            <Alert
              icon={false}
              color="success"
              onClose={() => setAlertVisibility(false)}
              sx={{
                backgroundColor: 'success.light',
                color: 'success.dark',
                mb: '15px',
              }}
            >
              Course Subscribed Successfully!
            </Alert>
          </Collapse>
        );
      }

      if (
        questyStringParams.get('paymentStatus') === 'canceled' &&
        !courseData.isSubscribed
      ) {
        return (
          <Collapse in={alertVisibility}>
            <Alert
              icon={false}
              color="warning"
              onClose={() => setAlertVisibility(false)}
              sx={{
                backgroundColor: 'warning.light',
                color: 'warning.dark',
                mb: '15px',
              }}
            >
              Course Subscription Canceled!
            </Alert>
          </Collapse>
        );
      }

      if (
        questyStringParams.get('paymentStatus') === 'failed' &&
        !courseData.isSubscribed
      ) {
        return (
          <Collapse in={alertVisibility}>
            <Alert
              icon={false}
              color="error"
              onClose={() => setAlertVisibility(false)}
              sx={{
                backgroundColor: 'error.light',
                color: 'error.dark',
                mb: '15px',
              }}
            >
              Course Subscription Failed!
            </Alert>
          </Collapse>
        );
      }
    }

    return null;
  };

  const breadcrumbOptions = constructBreadcrumbOptions();

  return (
    <>
      <Container maxWidth="xl">
        <div style={{ padding: '30px 0' }}>
          <Breadcrumbs
            options={breadcrumbOptions}
            textColor="#647488"
            showDropDown={isXsScreen}
          />
        </div>
        {renderAlerts()}
        {coursesState.status === 'loading' && !courseData ? (
          <BasicLoader />
        ) : null}
        <Grid container spacing={2} mb="15px">
          <Grid item lg={isSubscriptionVisible ? 8 : 12}>
            {courseData ? (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" flexGrow={1}>
                    <IconButton
                      style={{ marginRight: '5px' }}
                      onClick={toggleClassDetailsCollapse}
                    >
                      {isClassDetailsCollapseOpen ? (
                        <ExpandLessIcon style={{ color: TP_COLORS.black }} />
                      ) : (
                        <ExpandMoreIcon style={{ color: TP_COLORS.black }} />
                      )}
                    </IconButton>
                    <Typography variant="h5" fontSize="25px" fontWeight={700}>
                      {courseData.title}
                    </Typography>
                  </Stack>
                </Stack>
                <Collapse in={isClassDetailsCollapseOpen}>
                  <div
                    style={{ padding: `15px ${isXsScreen ? '15px' : '50px'}` }}
                  >
                    <div style={{ marginBottom: '15px' }}>
                      <div
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                          __html: courseData.description || '',
                        }}
                      />
                    </div>
                    <div>
                      <ClassDetailViewer course={courseData} />
                    </div>
                  </div>
                </Collapse>
                <div
                  style={{
                    height: '1px',
                    borderBottom: '1px dotted #94A3B8',
                    marginTop: '30px',
                  }}
                />
                <Stack direction="row" alignItems="center" mt="30px" mb="15px">
                  <IconButton
                    style={{ marginRight: '5px' }}
                    onClick={toggleClassListCollapse}
                  >
                    {isClassListCollapseOpen ? (
                      <ExpandLessIcon style={{ color: TP_COLORS.black }} />
                    ) : (
                      <ExpandMoreIcon style={{ color: TP_COLORS.black }} />
                    )}
                  </IconButton>
                  <Stack flexGrow={1}>
                    <Typography variant="h5" fontSize="25px" fontWeight={700}>
                      Class Details
                    </Typography>
                    <Typography
                      variant="caption"
                      fontSize="14px"
                      fontWeight={500}
                      color="#647488"
                    >
                      {numberOfClassesAndDurationText}
                    </Typography>
                  </Stack>
                  {courseSections.length ? (
                    <Button
                      variant="text"
                      color="info"
                      size="small"
                      onClick={handleCollapseAllSections}
                    >
                      {isAllSectionsOpened
                        ? 'Collapse all sections'
                        : 'Expand all sections'}
                    </Button>
                  ) : null}
                </Stack>
                <Collapse in={isClassListCollapseOpen}>
                  {sectionsState.status === 'loading' &&
                  !courseSections.length ? (
                    <div style={{ marginBottom: '15px' }}>
                      <BasicLoader />
                    </div>
                  ) : null}
                  <Stack spacing={2}>
                    {courseSections.map((item, index) => {
                      const _keyName = `${index}`;
                      const _orderNumber = index + 1;
                      const _collapse = openedSections.some(
                        (itemId) => itemId === item._id
                      );

                      return (
                        <Stack id={_keyName} key={_keyName}>
                          <SectionItem
                            openedSections={openedSections}
                            keyName={_keyName}
                            orderNumber={_orderNumber}
                            section={item}
                            collapse={_collapse}
                            toggleCollapse={handleSectionCollapse}
                          />
                        </Stack>
                      );
                    })}
                  </Stack>
                </Collapse>
              </>
            ) : null}
          </Grid>
          {courseData && isSubscriptionVisible ? (
            <Grid item lg={4}>
              <div style={{ position: 'sticky', top: '80px' }}>
                <SubscriptionCard course={courseData} />
              </div>
            </Grid>
          ) : null}
        </Grid>
      </Container>
    </>
  );
};

export default ViewCoursePage;
